import { EbayItem } from "@contracts/interfaces/ebay.interface";
import { FacebookItem } from "@contracts/interfaces/facebook.interface";
import { InventoryItem } from "@contracts/interfaces/inventory-item.interface";
import { WebflowItem } from "@contracts/interfaces/webflow.interface";
import { MediaService } from "../services/media.service";

class InventoryItemFactory {
    private static itemDefault: InventoryItem = {
        status: "empty",
        checkOutReason: "",
        number: 0,
        category: 5,
        title: "",
        description: "",
        dateCreated: new Date(),
        stockTake: null,
        features: [],
        media: [],
        decision: "",
        improvementRequest: "",
        log: [],
        lastModified: new Date(),
        sell: {
            price: 0,
            lowest: 0,
            reason: ""
        },
        lineItem: {
            item: "",
            desc: "",
            price: 0,
            qty: 1,
        },
        ebayImages: [],
        ebayBestOffers: false,
    }

    private static ebayDefaults: EbayItem = {
        ItemID: 0,
        SKU: 0,
        Title: "",
        Description: "",
        StartPrice: 0,
        CategoryMappingAllowed: true,
        PrimaryCategory: { CategoryID: 0 },
        ConditionID: 0,
        Country: "AU",
        Currency: "AUD",
        Brand: "",
        DispatchTimeMax: 2,
        ListingDuration: 'GTC',
        ListingType: 'FixedPriceItem',
        ItemSpecifics: { NameValueList: [] },
        PictureDetails: { PictureURL: [] },
        PostalCode: '3555',
        Quantity: 1,
        ReturnPolicy: {
            ReturnsAcceptedOption: 'ReturnsAccepted',
            RefundOption: 'MoneyBack',
            ReturnsWithinOption: 'Days_30',
            ShippingCostPaidByOption: 'Buyer',
        },
        ShippingDetails: {
            ShippingServiceOptions: [{
                ShippingServicePriority: 1,
                ShippingService: 'AU_Regular',
                ShippingServiceCost: 0
            }]
        },
        Site: "Australia",
        BestOfferDetails: {
            BestOfferEnabled: false
        },
        ListingDetails: {
            BestOfferAutoAcceptPrice: 0,
            MinimumBestOfferPrice: 0
        }
    }

    private static facebookDefaults: FacebookItem = {
        title: "",
        description: "",
        url: "",
        price: 0
    }

    private static webflowDefaults: WebflowItem = {
        title: "",
        isListed: false
    }

    public static validate(item: InventoryItem): InventoryItem {
        
        if (item.status == undefined) item.status = this.itemDefault.status;
        
        item.checkOutReason = item.checkOutReason || this.itemDefault.checkOutReason;
        item.number = item.number || this.itemDefault.number;
        item.category = item.category != undefined ? item.category : this.itemDefault.category;
        item.title = item.title || this.itemDefault.title;
        item.description = item.description || this.itemDefault.description
        item.dateCreated = item.dateCreated || this.itemDefault.dateCreated;
        item.features = item.features || this.itemDefault.features;
        item.decision = item.decision || this.itemDefault.decision;
        item.improvementRequest = item.improvementRequest || this.itemDefault.improvementRequest;
        item.log = item.log || this.itemDefault.log;
        item.lastModified = item.lastModified || this.itemDefault.lastModified;
        item.lineItem = item.lineItem || this.itemDefault.lineItem;
        item.ebayImages = item.ebayImages || this.itemDefault.ebayImages;
        item.stockTake = item.stockTake || this.itemDefault.stockTake;

        item.media = item.media || this.itemDefault.media;

        if (!item.sell) {
            item.sell = this.itemDefault.sell;
        }

        if (!item.ebay) {
            item.ebay = this.ebayDefaults;
        } else {
            const e = item.ebay;

            item.ebay.ItemID = e.ItemID || this.ebayDefaults.ItemID;
            item.ebay.SKU = e.SKU || this.ebayDefaults.SKU;
            item.ebay.Title = e.Title || this.ebayDefaults.Title;
            item.ebay.Description = e.Description || this.ebayDefaults.Description;
            item.ebay.StartPrice = e.StartPrice || this.ebayDefaults.StartPrice;
            item.ebay.CategoryMappingAllowed = e.CategoryMappingAllowed || this.ebayDefaults.CategoryMappingAllowed;
            item.ebay.PrimaryCategory = e.PrimaryCategory || this.ebayDefaults.PrimaryCategory;
            item.ebay.ConditionID = e.ConditionID || this.ebayDefaults.ConditionID;
            item.ebay.Country = e.Country || this.ebayDefaults.Country;
            item.ebay.Currency = e.Currency || this.ebayDefaults.Currency;
            item.ebay.Brand = e.Brand || this.ebayDefaults.Brand;
            item.ebay.DispatchTimeMax = e.DispatchTimeMax || this.ebayDefaults.DispatchTimeMax;
            item.ebay.ListingDuration = e.ListingDuration || this.ebayDefaults.ListingDuration;
            item.ebay.ListingType = e.ListingType || this.ebayDefaults.ListingType;
            item.ebay.ItemSpecifics = e.ItemSpecifics || this.ebayDefaults.ItemSpecifics;
            item.ebay.PictureDetails = e.PictureDetails || this.ebayDefaults.PictureDetails;
            item.ebay.PostalCode = e.PostalCode || this.ebayDefaults.PostalCode;
            item.ebay.Quantity = e.Quantity || this.ebayDefaults.Quantity;
            item.ebay.ReturnPolicy = e.ReturnPolicy || this.ebayDefaults.ReturnPolicy;
            item.ebay.ShippingDetails = e.ShippingDetails || this.ebayDefaults.ShippingDetails;
            item.ebay.Site = e.Site || this.ebayDefaults.Site;
            item.ebay.BestOfferDetails = e.BestOfferDetails || this.ebayDefaults.BestOfferDetails;
            item.ebay.ListingDetails = e.ListingDetails || this.ebayDefaults.ListingDetails;
        }

        if (!item.facebook) {
            item.facebook = this.facebookDefaults;
        } else {
            const e = item.facebook;

            item.facebook.title = e.title || this.facebookDefaults.title;
            item.facebook.description = e.description || this.facebookDefaults.description;
            item.facebook.url = e.url || this.facebookDefaults.url;
            item.facebook.price = e.price || this.facebookDefaults.price;
        }

        if (!item.webflow) {
            item.webflow = this.webflowDefaults
        } else {
            const e = item.webflow;

            item.webflow.title = e.title || this.webflowDefaults.title;
            item.webflow.isListed = e.isListed || this.webflowDefaults.isListed;
        }

        return item;
    }

    public static new (category: number = 5) {
        this.itemDefault.category = category;
        return this.itemDefault;
    }

    public static newFacebook () {
        return this.facebookDefaults;
    }

    public static newWebflow() {
        return this.webflowDefaults;
    }

    public static newEbay () {
        return this.ebayDefaults;
    }
}

export { InventoryItemFactory }